"use client";

import React, { useState } from "react";
import Image from "next/image";
import { IMAGE_SIZES } from "@/lib/imageSizes";
import { cn } from "@/lib/utils";

type Props = {
  name: string;
  imageUrl: string | null;
  className?: string;
  imageClassName?: string;
  textClassName?: string;
};

const getInitials = (name: string) => {
  const words = name
    .replace(/[^\p{L}\p{N}\s]/gu, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (words.length === 0) {
    return "?";
  }

  // One-word names read better as two characters ("123ERAmakelaar" -> "12").
  const initials =
    words.length === 1
      ? words[0].slice(0, 2)
      : words
          .slice(0, 2)
          .map((word) => word[0])
          .join("");

  return initials.toUpperCase();
};

/**
 * Owner logo with an initials fallback. A large share of owner records point at
 * logo files that no longer resolve, which used to leave an empty white box, so
 * a failed load swaps in the monogram instead.
 */
const OwnerLogo: React.FC<Props> = ({
  name,
  imageUrl,
  className,
  imageClassName,
  textClassName,
}) => {
  const [failed, setFailed] = useState(false);
  const showImage = Boolean(imageUrl) && !failed;

  return (
    <div
      className={cn(
        "flex flex-none items-center justify-center overflow-hidden rounded-2xl border border-border bg-white shadow-sm",
        className,
      )}
    >
      {showImage ? (
        <Image
          src={imageUrl as string}
          alt={name}
          width={192}
          height={192}
          sizes={IMAGE_SIZES.thumb96}
          onError={() => setFailed(true)}
          className={cn("size-full object-contain p-1.5", imageClassName)}
        />
      ) : (
        <span
          aria-hidden
          className={cn(
            "flex size-full items-center justify-center bg-secondary text-lg font-semibold tracking-tight",
            textClassName,
          )}
        >
          {getInitials(name)}
        </span>
      )}
    </div>
  );
};

export default OwnerLogo;
